import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { Chat, InteractionTypeEnum } from '@trinity/chat';
import { ChatCitationType, ReportFormat } from '@trinity/chat';
import {
  AssignCitationRefContext,
  OpenCrmNoteContext,
  OpenSnippetContext,
  SetOpenCitationContext,
} from '@trinity/chat-components/components/Chat/context';
import { useDownloadKolProfile } from '@trinity/chat-components/components/Chat/hooks/useDownloadKolProfile';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown.js';
import { CitationNumber } from '@trinity/chat-components/components/presentation/CitationNumber';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { trunc } from '@trinity/chat-components/utils';
import { CrmNote } from './Citation.CrmNote';
import { DashboardCitation } from './Citation.Dashboard';
import { DataAgentQueryResultsCitation } from './Citation.DataAgentQueryResults';
import { HcpProfileCitation } from './Citation.HcpProfile';
import {
  AffiliationDetails,
  AssociationDetails,
  ClinicalLeaderTopicDetails,
  ClinicalTrialDetails,
  CollaborationDetails,
  CompanySponsorshipDetails,
  ConferenceDetails,
  ContactDetailsCitation,
  DigialLeaderTopicDetails,
  PublicationDetails,
  ScientificLeaderTopicDetails,
  UnmetNeedDetails,
  RecommendationDetails,
} from './Citation.HcpProfile.Elements';
import { MessageCitationMaybeLink } from './Citation.MaybeLink';
import { QuantAnalysesCitation } from './Citation.QuantAnalyses';
import { MessageCitationSnippet, SnippetRoot, SnippetText } from './Citation.Snippet';
import { MessageCitationSnippetGroupSelector } from './Citation.SnippetGroupSelector';
import { SurveyRespondentsCitation } from './Citation.SurveyRespondents';
import { StyledActionButton, ToggleCitation } from './Citation.Toggle';
import { getSnippetLink, getSnippetDisplayValue, buildSnippetCharacters } from './utils';

function renderCitationByType(item: Chat.Citation, isCitationOpen: boolean) {
  if (item.type === ChatCitationType.KolProfile && item.metadata) {
    return <HcpProfileCitation value={item.metadata as Chat.KolCitationMetadata} />;
  }

  if (!isCitationOpen) {
    return null;
  }

  if (item.type === ChatCitationType.CrmNote) {
    return <CrmNote data={item.metadata as Chat.CrmNoteCitationMetadata} />;
  } else if (item.type === ChatCitationType.StrategicImperative) {
    return (
      <StyledSnippetRoot>
        <SnippetText>
          <BasicMarkdown>{(item.metadata as Chat.StrategicImperativeCitationMetadata).content}</BasicMarkdown>
        </SnippetText>
      </StyledSnippetRoot>
    );
  } else if (item.type === ChatCitationType.ContactDetail) {
    return (
      <ContactDetailsCitation data={item.metadata as Chat.CitationMetadata<ChatCitationType.ContactDetail>} />
    );
  } else {
    return null;
  }
}

function renderCrmNoteSnippet(item: Chat.Citation, meta: unknown): React.ReactNode {
  switch (item.type) {
    case ChatCitationType.CallCenterInteractions:
    case ChatCitationType.FbmsInteractions:
      return <CrmNote data={meta as Chat.CitationSnippetMetadata<ChatCitationType.CallCenterInteractions>} />;
    case ChatCitationType.MedicalInsights:
      return <CrmNote data={meta as Chat.CrmNoteCitationMetadata<InteractionTypeEnum.MedicalInsight>} />;
    case ChatCitationType.DocumentedInterests:
      return <CrmNote data={meta as Chat.CrmNoteCitationMetadata<InteractionTypeEnum.DocumentedInterest>} />;
    default:
      return null;
  }
}

const crmNoteTypes = new Set([
  ChatCitationType.CallCenterInteractions,
  ChatCitationType.FbmsInteractions,
  ChatCitationType.MedicalInsights,
  ChatCitationType.DocumentedInterests,
]);

function renderLeaderTopicDetails(item: Chat.Citation, meta: unknown): React.ReactNode {
  switch (item.type) {
    case ChatCitationType.ScientificLeaderTopic:
      return (
        <ScientificLeaderTopicDetails
          data={meta as Chat.CitationSnippetMetadata<ChatCitationType.ScientificLeaderTopic>}
        />
      );
    case ChatCitationType.ClinicalLeaderTopic:
      return (
        <ClinicalLeaderTopicDetails
          data={meta as Chat.CitationSnippetMetadata<ChatCitationType.ClinicalLeaderTopic>}
        />
      );
    case ChatCitationType.DigitalLeaderTopic:
      return (
        <DigialLeaderTopicDetails
          data={meta as Chat.CitationSnippetMetadata<ChatCitationType.DigitalLeaderTopic>}
        />
      );
    default:
      return null;
  }
}

type SnippetDetailsRenderer = (item: Chat.Citation, selectedSnippet: Chat.CitationSnippet) => React.ReactNode;

const snippetDetailsRenderers: Partial<Record<ChatCitationType, SnippetDetailsRenderer>> = {
  [ChatCitationType.Publication]: (_, selectedSnippet) => (
    <PublicationDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Publication>}
    />
  ),
  [ChatCitationType.CongressAbstract]: (_, selectedSnippet) => (
    <PublicationDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Publication>}
    />
  ),
  [ChatCitationType.ClinicalTrial]: (_, selectedSnippet) => (
    <ClinicalTrialDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.ClinicalTrial>}
    />
  ),
  [ChatCitationType.Association]: (_, selectedSnippet) => (
    <AssociationDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Association>}
    />
  ),
  [ChatCitationType.Affiliation]: (_, selectedSnippet) => (
    <AffiliationDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Affiliation>}
    />
  ),
  [ChatCitationType.Collaboration]: (_, selectedSnippet) => (
    <CollaborationDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Collaboration>}
    />
  ),
  [ChatCitationType.CompanySponsorship]: (_, selectedSnippet) => (
    <CompanySponsorshipDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.CompanySponsorship>}
    />
  ),
  [ChatCitationType.Conference]: (_, selectedSnippet) => (
    <ConferenceDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Conference>}
    />
  ),
  [ChatCitationType.ScientificLeaderTopic]: (item, selectedSnippet) =>
    renderLeaderTopicDetails(item, selectedSnippet.metadata),
  [ChatCitationType.ClinicalLeaderTopic]: (item, selectedSnippet) =>
    renderLeaderTopicDetails(item, selectedSnippet.metadata),
  [ChatCitationType.DigitalLeaderTopic]: (item, selectedSnippet) =>
    renderLeaderTopicDetails(item, selectedSnippet.metadata),
  [ChatCitationType.UnmetNeed]: (_, selectedSnippet) => (
    <UnmetNeedDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.UnmetNeed>}
    />
  ),
  [ChatCitationType.Recommendation]: (_, selectedSnippet) => (
    <RecommendationDetails
      data={selectedSnippet.metadata as Chat.CitationSnippetMetadata<ChatCitationType.Recommendation>}
    />
  ),
};

// Survey citations built before the capture sheet layout have no question count; they render generically.
function isSurveyCitation(item: Chat.Citation): item is Chat.Citation<ChatCitationType.SurveyQuestionResponses> {
  return (
    item.type === ChatCitationType.SurveyQuestionResponses &&
    (item.metadata as Chat.SurveyCitationMetadata).questionCount !== undefined
  );
}

function renderDetailsByType(item: Chat.Citation, selectedSnippet: Chat.CitationSnippet): React.ReactNode {
  if (isSurveyCitation(item)) {
    return (
      <SurveyRespondentsCitation
        metadata={selectedSnippet.metadata as Chat.SurveyCitationSnippetMetadata}
        baseUrl={item.baseUrl}
        to={getSnippetLink(item, selectedSnippet)}
      />
    );
  }

  if (item.type === ChatCitationType.Dashboard) {
    return (
      <DashboardCitation
        snippet={selectedSnippet as Chat.CitationSnippet<ChatCitationType.Dashboard>}
        link={item.link}
      />
    );
  }

  if (item.type === ChatCitationType.QuantAnalysis) {
    return (
      <QuantAnalysesCitation
        value={selectedSnippet.metadata as Chat.CitationSnippetMetadataQuantAnalysis}
        to={item.link}
      />
    );
  }

  if (item.type === ChatCitationType.DataAgentQueryResults) {
    return (
      <DataAgentQueryResultsCitation
        item={item as Chat.Citation<ChatCitationType.DataAgentQueryResults>}
        snippet={selectedSnippet as Chat.CitationSnippet<ChatCitationType.DataAgentQueryResults>}
      />
    );
  }

  const renderDetails = snippetDetailsRenderers[item.type];
  if (renderDetails) {
    return renderDetails(item, selectedSnippet);
  }

  return null;
}

function renderSnippetByType(item: Chat.Citation, selectedSnippet: Chat.CitationSnippet): React.ReactNode {
  if (crmNoteTypes.has(item.type)) {
    return renderCrmNoteSnippet(item, selectedSnippet.metadata);
  }
  return renderDetailsByType(item, selectedSnippet);
}

function renderTitle(item: Chat.Citation): React.ReactNode {
  if (item.type === ChatCitationType.DataAgentQueryResults) {
    return trunc(item.title, 50);
  }

  if (isSurveyCitation(item)) {
    return (
      <>
        {trunc(item.title, 50)} ({item.metadata.questionCount})
      </>
    );
  }

  return (
    <>
      {trunc(item.title, 50)} {item.snippets.length > 1 && <>({item.snippets.length})</>}
    </>
  );
}

type Props = {
  className?: string;
  item: Chat.Citation;
};

function buildCitationKey(item: Chat.Citation) {
  return `${item.type}-${item.id}`;
}

export const MessageCitation = ({ className, item }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const [openSnippet, handleSnippetSelection] = useContext(OpenSnippetContext);
  const assignCitationRef = useContext(AssignCitationRefContext);

  const openCitation = useContext(OpenCrmNoteContext);
  const setOpenCitation = useContext(SetOpenCitationContext);

  const onSnippetSelected = useCallback(
    (snippetId: string) => () => {
      handleSnippetSelection([buildCitationKey(item), snippetId]);
    },
    [handleSnippetSelection, item]
  );

  const citationKey = useMemo(() => buildCitationKey(item), [item]);

  const selectedSnippet = useMemo(() => {
    if (!openSnippet) return null;
    if (citationKey !== openSnippet[0]) return null;

    return item.snippets.find((snippet) => snippet.id === openSnippet[1]);
  }, [citationKey, item.snippets, openSnippet]);

  const setCitationRef = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const itemKey = buildCitationKey(item);
        assignCitationRef(itemKey, node);
        ref.current = node;
      }
    },
    [assignCitationRef, item]
  );

  const isCitationOpen = useMemo(() => {
    return openCitation === item.id || openSnippet?.[0] === citationKey;
  }, [openCitation, item.id, openSnippet, citationKey]);

  const renderOpenCitation = useCallback(() => {
    return renderCitationByType(item, isCitationOpen);
  }, [item, isCitationOpen]);

  const renderOpenSnippet = useCallback(() => {
    if (isCitationOpen && item.content && !selectedSnippet) {
      return <MessageCitationSnippet content={item.content} baseUrl={item.baseUrl} to={item.link} />;
    }

    if (!selectedSnippet) return null;

    const typeComponent = renderSnippetByType(item, selectedSnippet);
    if (typeComponent) return typeComponent;

    return (
      <MessageCitationSnippet
        content={selectedSnippet.content ?? selectedSnippet.text}
        to={getSnippetLink(item, selectedSnippet)}
      />
    );
  }, [isCitationOpen, item, selectedSnippet]);

  const toggleCitationOpen = useCallback(() => {
    const value = isCitationOpen ? null : item.id;
    setOpenCitation(value);
    if (!value) {
      handleSnippetSelection(null);
    }
  }, [handleSnippetSelection, isCitationOpen, item.id, setOpenCitation]);

  return (
    <Root className={className} ref={setCitationRef}>
      <Wrap>
        <Header>
          {!!item.ordinal && <CitationOrdinal>{item.ordinal}</CitationOrdinal>}
          <Layers>
            <Layer1>
              <FlexRow>
                {item.type === ChatCitationType.Web && (
                  <WebCitationFavicon
                    domain={(item.metadata as Chat.WebCitationMetadata).domain}
                    baseUrl={item.baseUrl}
                  />
                )}
                <StyledLink to={item.link} baseUrl={item.baseUrl}>
                  <Tooltip title={item.title} enterDelay={1000} enterNextDelay={500}>
                    <Title>{renderTitle(item)}</Title>
                  </Tooltip>
                </StyledLink>
                {item.type === ChatCitationType.KolProfile && item.metadata && (
                  <DownloadProfile data={item.metadata as Chat.KolCitationMetadata} />
                )}
                {<ToggleCitation type={item.type} isOpen={isCitationOpen} setOpen={toggleCitationOpen} />}
              </FlexRow>
            </Layer1>
            <Layer2>
              {renderOpenCitation()}
              {isCitationOpen && !!item.snippets.length && (
                <SnippetGroupSelectorContainer>
                  {item.snippets.map((snippet, i) => {
                    const isSelected = selectedSnippet?.id === snippet.id;
                    const displayValue = getSnippetDisplayValue(snippet, item.type);
                    return (
                      <SnippetWrapper key={i}>
                        {!!snippet.ordinal && (
                          <SnippetOrdinal>{buildSnippetCharacters(snippet.ordinal)}</SnippetOrdinal>
                        )}
                        <MessageCitationSnippetGroupSelector
                          value={displayValue}
                          selected={isSelected}
                          onClick={onSnippetSelected(snippet.id)}
                        />
                      </SnippetWrapper>
                    );
                  })}
                </SnippetGroupSelectorContainer>
              )}
            </Layer2>
          </Layers>
        </Header>
        {renderOpenSnippet()}
      </Wrap>
    </Root>
  );
};

type DownloadProfileProps = {
  data: Chat.KolCitationMetadata;
};

const DownloadProfile = ({ data }: DownloadProfileProps) => {
  const { download } = useDownloadKolProfile({
    kol: {
      id: data.kolId,
      name: data.kolName,
    },
  });

  const handleDownload = useCallback(async () => {
    await download({
      title: `Generating Export`,
      name: `${data.kolName} Profile`,
      extension: ReportFormat.Word,
    });
  }, [download, data.kolName]);

  return <StyledActionButton onClick={handleDownload}>[Download Profile]</StyledActionButton>;
};

const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
);

const WebCitationFavicon = ({ domain, baseUrl }: { domain: string; baseUrl?: string }) => {
  const [faviconFailed, setFaviconFailed] = useState(false);

  const src = baseUrl ? `${baseUrl}/v1/proxy/favicon?domain=${encodeURIComponent(domain)}` : null;

  return (
    <FaviconWrap>
      {faviconFailed || !src ? (
        <GlobeIcon />
      ) : (
        <img src={src} width={16} height={16} alt="" onError={() => setFaviconFailed(true)} />
      )}
    </FaviconWrap>
  );
};

const FaviconWrap = styled.div({
  flexShrink: 0,
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 6,
  color: 'inherit',
  opacity: 0.6,
});

const Root = styled.div`
  box-sizing: border-box;
`;

const Wrap = styled.div`
  font-size: 12px;
  display: flex;
  flex-direction: column;
`;

export const Header = styled.div({
  padding: `12px 5px`,
  display: 'flex',
  alignItems: 'flex-start',
});

const Layer1 = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Layer2 = styled.div({});

const FlexRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

export const Title = styled.div`
  font-family: ${(props) => props.theme.fonts.semiBold};
  font-size: 13px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const SnippetGroupSelectorContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;

  margin-top: 10px;
`;

const SnippetWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const StyledLink = styled(MessageCitationMaybeLink)`
  flex: 1;
  min-width: 0;
`;

const Layers = styled.div({
  width: 'calc(100% - 30px)',
  display: 'flex',
  flexDirection: 'column',
});

const CitationOrdinal = styled(CitationNumber)({
  marginRight: 10,
});

const SnippetOrdinal = styled(CitationOrdinal)({
  marginRight: 5,
});

const StyledSnippetRoot = styled(SnippetRoot)({
  marginTop: 10,
});
